import type { ProductInfo, SalesChecklist } from "@expert/contentful";
import type { AudioType } from "@expert/shared-types";
import type { LocaleCode } from "contentful";

import { Accordion, Divider, Flex, ScrollArea, Text } from "@mantine/core";
import { type JSX, useCallback, useMemo, useState } from "react";

import { useSalesContext } from "../../../../sales-context/SalesContext";
import { useOfferChecklistStore } from "../../../../stores/useOfferChecklistStore";
import { DynamicCoreBenefits } from "../../../dynamic-smart-offer/DynamicCoreBenefits/DynamicCoreBenefits";
import { DynamicToneGuidance } from "../../../dynamic-smart-offer/DynamicToneGuidance/DynamicToneGuidance";
import { useDynamicSmartOffer } from "../../../dynamic-smart-offer/hooks/useDynamicSmartOffer";
import { ChecklistIcon } from "../Icons";
import { LanguageSelector } from "../language-selector/LanguageSelector";
import { ChecklistSectionWithErrorBoundary } from "./components/ChecklistSectionWithErrorBoundary";
import { GuidedBrowsingContent } from "./components/GuidedBrowsingContent";
import { RecordedLegalDisclosuresSection } from "./components/RecordedLegalDisclosuresSection";
import { isSectionComplete } from "./utils/isSectionComplete";

export type FullOfferChecklistProps = {
    salesChecklist: SalesChecklist;
    product?: Record<LocaleCode, ProductInfo>;
    productSku: string;
    sendLinkCopy?: string;
    sentSuccessMessage?: string;
    onDisclosureCompleted?: (audioType: AudioType) => void;
    onDisclosureAudioFailed?: () => void;
    isGuidedBrowsing?: boolean;
    checklistPlacement?: string;
    requiresChecklistCompletion?: boolean;
    returnLinkToExpert?: boolean;
    sendLinkCommType?: string;
};

export function FullOfferChecklist({
    salesChecklist,
    product: _product,
    productSku,
    sendLinkCopy,
    sentSuccessMessage,
    onDisclosureCompleted = () => {},
    onDisclosureAudioFailed = () => {},
    isGuidedBrowsing,
    checklistPlacement,
    requiresChecklistCompletion,
    returnLinkToExpert,
    sendLinkCommType,
}: FullOfferChecklistProps): JSX.Element | null {
    const { sessionId, partner, logger, analytics, features } = useSalesContext();
    const {
        data: dynamicSmartOfferData,
        isPending,
        isAwaitingData,
        isFallback,
        isEnabled,
        showLatest,
        hasNewerData,
    } = useDynamicSmartOffer({
        sessionId,
        salesChecklist,
        partner,
        dispatcher: analytics.dispatcher,
        logger,
        isDynamicSmartOfferEnabled: features.isDynamicSmartOfferEnabled,
    });

    const effectivePlacement = useMemo(() => {
        const preferred = checklistPlacement ?? "TRANSITION";
        if (preferred === "TRANSITION") return "TRANSITION";
        const found = salesChecklist.checklistSections.some((s) =>
            s.checklistItems.some((item) => (item.additionalRenderType ?? item.key) === preferred),
        );
        return found ? preferred : "TRANSITION";
    }, [checklistPlacement, salesChecklist]);

    const firstSectionToOpen = useMemo(
        () =>
            salesChecklist.checklistSections.find(
                (section) =>
                    section.checklistItems.length !== 0 &&
                    !isSectionComplete(
                        section.checklistItems,
                        useOfferChecklistStore.getState().getData(sessionId, productSku),
                    ),
            )?.title,
        [salesChecklist, sessionId, productSku],
    );

    const [accordionValue, setAccordionValue] = useState<string[] | undefined>(
        firstSectionToOpen ? [firstSectionToOpen, "recorded-legal-disclosures"] : ["recorded-legal-disclosures"],
    );

    const [disclosureEndTime, setDisclosureEndTime] = useState<Date | null>(null);

    const handleSetAccordionValue = (value: string[]) => {
        const openedSection = value.find((section) => !accordionValue?.includes(section));
        if (openedSection) {
            void analytics.dispatcher.dispatchUserAction("SalesChecklistSection_Opened", openedSection, {
                sessionId,
                partner,
            });
        }
        setAccordionValue(value);
    };

    const onSectionComplete = useCallback(
        (title: string) => {
            setAccordionValue((prevState) => {
                const indexOfCompletedSection = salesChecklist.checklistSections.findIndex(
                    (section) => section.title === title,
                );
                const completedSection = salesChecklist.checklistSections[indexOfCompletedSection];
                const nextTitle = salesChecklist.checklistSections.at(indexOfCompletedSection + 1)?.title;
                if (nextTitle) {
                    prevState?.push(nextTitle);
                }

                // Don't close the section if GuidedBrowsingContent is placed here
                const hasGuidedBrowsingPlacement = completedSection
                    ? completedSection.checklistItems.some(
                          (item) => (item.additionalRenderType ?? item.key) === effectivePlacement,
                      )
                    : false;
                if (prevState?.includes(title) && !hasGuidedBrowsingPlacement) {
                    prevState = prevState.filter((t) => t !== title);
                }
                return prevState;
            });
        },
        [salesChecklist.checklistSections, effectivePlacement],
    );

    return (
        <Flex data-testid="step-two-content" direction="column" w="100%">
            <ScrollArea h="calc(100% - var(--customer-panel-bottom-control-footer-height))">
                <Flex id="full-offer-checklist-header" c="dark.2" p={"sm"} pb="xxs" justify="space-between">
                    <Flex align="center">
                        <ChecklistIcon />
                        <Text fw={700} fz="xs" pl="3xs">
                            OFFER CHECKLIST
                        </Text>
                    </Flex>
                    <LanguageSelector />
                </Flex>

                <Divider size="xs" color="dark.4" />

                <DynamicToneGuidance
                    isEnabled={isEnabled}
                    isPending={isPending}
                    isAwaitingData={isAwaitingData}
                    hasNewerData={hasNewerData}
                    isFallback={isFallback}
                    toneGuidance={dynamicSmartOfferData.toneGuidance}
                    onShowLatest={showLatest}
                />
                <Accordion transitionDuration={250} multiple value={accordionValue} onChange={handleSetAccordionValue}>
                    {salesChecklist.checklistSections.map((section) => {
                        if (section.checklistItems.length === 0) return null;
                        // RDESQ-1988: when flag is ON, Discovery/Engagement sections are pushed
                        // to the Expert Assist timeline instead of rendering in the checklist.
                        if (
                            features.isUsingMovedDiscoveryAndEngagementQuestions &&
                            (section.sectionType === "discovery" || section.sectionType === "engagement")
                        ) {
                            return null;
                        }

                        const hasGuidedBrowsingPlacement = section.checklistItems.some(
                            (item) => (item.additionalRenderType ?? item.key) === effectivePlacement,
                        );

                        return (
                            <ChecklistSectionWithErrorBoundary
                                key={section.id}
                                section={section}
                                productSku={productSku}
                                onSectionComplete={onSectionComplete}
                                prependSlot={
                                    isDynamicCoreBenefitsSection(section) ? (
                                        <DynamicCoreBenefits
                                            isEnabled={isEnabled}
                                            isPending={isPending}
                                            isAwaitingData={isAwaitingData}
                                            pitchPoints={dynamicSmartOfferData.pitchPoints}
                                        />
                                    ) : undefined
                                }
                                appendSlot={
                                    hasGuidedBrowsingPlacement && isGuidedBrowsing ? (
                                        <GuidedBrowsingContent
                                            productSku={productSku}
                                            sendLinkCopy={sendLinkCopy}
                                            sentSuccessMessage={sentSuccessMessage}
                                            checklistPlacement={effectivePlacement}
                                            requiresChecklistCompletion={requiresChecklistCompletion}
                                            returnLinkToExpert={returnLinkToExpert}
                                            sendLinkCommType={sendLinkCommType}
                                        />
                                    ) : undefined
                                }
                                hiddenItemKeys={
                                    isDynamicCoreBenefitsSection(section) && isEnabled ? ["COVERAGE"] : undefined
                                }
                            />
                        );
                    })}
                    <RecordedLegalDisclosuresSection
                        productSku={productSku}
                        disclosureEndTime={disclosureEndTime}
                        onSetDisclosureEndTime={setDisclosureEndTime}
                        onDisclosureCompleted={onDisclosureCompleted}
                        onDisclosureAudioFailed={onDisclosureAudioFailed}
                    />
                </Accordion>
            </ScrollArea>
        </Flex>
    );
}

function isDynamicCoreBenefitsSection(section: { sectionType?: string; enableDynamicContent?: boolean }) {
    return section.sectionType === "coreBenefits" && section.enableDynamicContent;
}
